import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Music, Users, DollarSign, BookOpen } from "lucide-react";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme-aware logos (SVG with transparent background)
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/drr8t3sh_Dark_2.svg";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_glass-teach/artifacts/6ix3baet_Light_2.svg";

export const LandingPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const logoUrl = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("mms_token", response.data.access_token);
      localStorage.setItem("mms_user_type", response.data.user_type);
      localStorage.setItem("mms_user_id", response.data.user_id);
      toast.success("Login successful!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Music, title: "20 Courses", desc: "From Drums to Jazz Theory" },
    { icon: Users, title: "Student Management", desc: "Track all your students" },
    { icon: DollarSign, title: "Auto Payroll", desc: "$27.50/hr or $22/30min" },
    { icon: BookOpen, title: "Course Materials", desc: "Access teaching resources" },
  ];

  return (
    <div className={`min-h-screen relative overflow-hidden ${theme === 'dark' ? 'bg-zinc-950' : 'bg-slate-50'}`}>
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {theme === 'dark' ? (
          <>
            <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-gradient-to-r from-zinc-800 to-zinc-900 rounded-full blur-3xl opacity-20" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-gradient-to-l from-zinc-800 to-zinc-900 rounded-full blur-3xl opacity-20" />
          </>
        ) : (
          <>
            <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-gradient-to-r from-indigo-100 to-blue-100 rounded-full blur-3xl opacity-50" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-gradient-to-l from-purple-100 to-indigo-100 rounded-full blur-3xl opacity-50" />
          </>
        )}
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-4">
          <img src={logoUrl} alt="MMS Logo" className="h-14 w-auto object-contain logo-glass" />
          <span className={`text-xl font-semibold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
            Muller Music School
          </span>
        </div>
        <div className="flex items-center gap-4">
          <ThemeToggle />
          <button
            data-testid="admin-login-btn"
            onClick={() => navigate("/admin-login")}
            className="glass-button-ghost text-sm"
          >
            Admin
          </button>
          <button
            data-testid="login-btn"
            onClick={() => setShowLogin(true)}
            className="glass-button-secondary text-sm py-2 px-5"
          >
            Sign In
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 max-w-7xl mx-auto px-8 pt-20 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h1 className={`text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
            <span className="text-gradient">Instructor Portal</span>
          </h1>
          <p className={`text-lg mb-10 max-w-xl mx-auto ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
            Manage your lessons, track payments, and access course materials all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="get-started-btn"
              onClick={() => navigate("/register")}
              className="glass-button-primary text-lg"
            >
              Get Started
            </button>
            <button
              data-testid="sign-in-hero-btn"
              onClick={() => setShowLogin(true)}
              className="glass-button-secondary text-lg"
            >
              Sign In
            </button>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="glass-card-hover p-8 group"
            >
              <feature.icon className={`w-10 h-10 mb-4 group-hover:scale-110 transition-transform ${theme === 'dark' ? 'text-white' : 'text-indigo-600'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>{feature.title}</h3>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </main>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className={`absolute inset-0 backdrop-blur-sm ${theme === 'dark' ? 'bg-black/60' : 'bg-black/40'}`}
            onClick={() => setShowLogin(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative glass-card p-8 w-full max-w-md"
          >
            <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>Welcome Back</h2>
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="form-label">Email</label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="glass-input w-full"
                  placeholder="instructor@example.com"
                  required
                />
              </div>
              <div>
                <label className="form-label">Password</label>
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="glass-input w-full"
                  placeholder="••••••••"
                  required
                />
              </div>
              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="glass-button-primary w-full disabled:opacity-50"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
            <p className={`text-center text-sm mt-6 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              New instructor?{" "}
              <button
                onClick={() => {
                  setShowLogin(false);
                  navigate("/register");
                }}
                className={`hover:underline ${theme === 'dark' ? 'text-white' : 'text-indigo-600'}`}
              >
                Register here
              </button>
            </p>
          </motion.div>
        </div>
      )}
    </div>
  );
};
